import React, { useState, useEffect } from 'react';
import { Product } from '@inter-ikea-storageone/range';
import theme from 'ui/theme';
import styled from 'styled-components';
import { StoreAvailabilityProbabilityEnum } from '@inter-ikea-kompis/enums/lib';
import { useAvailability } from 'contexts/availability';
import { useServiceSettings } from 'contexts/serviceSettings';

const Dot = styled.span`
  width: 0.25rem;
  height: 0.25rem;
  display: block;
  min-width: 0.5rem;
  min-height: 0.5rem;
  border-radius: 4rem;
  flex-shrink: 0;
  position: relative;
  transform: scale(0.9);
  margin-right: 0.5rem;
  border-style: solid;
  margin-top: 0.3rem;
`;
const DotAfter = styled.span`
  position: absolute;
  top: 50%;
  bottom: -0.0625rem;
  left: -0.0625rem;
  right: -0.0625rem;
  content: '';
  display: block;
  border-radius: 0 0 50% 50%;
  background-color: ${theme.colors.semantic.caution};
  box-sizing: inherit;
  outline: none;
`;

const StoreAvailability = styled.span`
  display: flex;
`;

type Props = {
  product: Product;
  bigIcon?: boolean;
};

const GetInStoreStockIndicator = ({ product, bigIcon }: Props) => {
  const [storeAvailability, setStoreAvailability] = useState<
    undefined | StoreAvailabilityProbabilityEnum
  >(undefined);

  const { translations } = useServiceSettings();

  const {
    storeAvailabilities,
    getProductStoreAvailability,
    getSelectedStoreName
  } = useAvailability();

  const storeName = getSelectedStoreName();

  useEffect(() => {
    const status = getProductStoreAvailability(product);
    setStoreAvailability(status);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [storeAvailabilities]);

  const storeAvailabilityInStockWithStore =
    translations.kompis.storeAvailabilityInStock &&
    translations.kompis.storeAvailabilityInStock.replace(`{{store}}`, storeName);
  const storeAvailabilityLowInStockWithStore =
    translations.kompis.storeAvailabilityLowInStock &&
    translations.kompis.storeAvailabilityLowInStock.replace(`{{store}}`, storeName);
  const storeAvailabilityOutOfStockWithStore =
    translations.kompis.storeAvailabilityOutOfStock &&
    translations.kompis.storeAvailabilityOutOfStock.replace(`{{store}}`, storeName);

  switch (storeAvailability) {
    case StoreAvailabilityProbabilityEnum.highInStock: {
      return (
        <StoreAvailability data-testid="store-availability-high">
          <Dot
            style={{
              backgroundColor: theme.colors.semantic.positive,
              borderColor: theme.colors.semantic.positive,
              borderWidth: bigIcon ? '0.125rem' : 0,
              height: bigIcon ? '0.75rem' : '0.25rem',
              width: bigIcon ? '0.75rem' : '0.25rem'
            }}
          />
          <span>{storeAvailabilityInStockWithStore}</span>
        </StoreAvailability>
      );
    }
    case StoreAvailabilityProbabilityEnum.mediumInStock:
    case StoreAvailabilityProbabilityEnum.lowInStock: {
      return (
        <StoreAvailability data-testid="store-availability-low">
          <Dot
            style={{
              backgroundColor: bigIcon ? theme.colors.transparent : theme.colors.semantic.caution,
              borderColor: theme.colors.semantic.caution,
              borderWidth: bigIcon ? '0.125rem' : 0,
              height: bigIcon ? '0.75rem' : '0.25rem',
              width: bigIcon ? '0.75rem' : '0.25rem'
            }}
          >
            {bigIcon && <DotAfter />}
          </Dot>

          <span>{storeAvailabilityLowInStockWithStore}</span>
        </StoreAvailability>
      );
    }
    case StoreAvailabilityProbabilityEnum.outOfStock: {
      return (
        <StoreAvailability data-testid="store-availability-out">
          <Dot
            style={{
              backgroundColor: bigIcon ? theme.colors.transparent : theme.colors.semantic.negative,
              borderColor: bigIcon ? theme.colors.semantic.negative : theme.colors.transparent,
              borderWidth: bigIcon ? '0.125rem' : 0,
              height: bigIcon ? '0.8rem' : '0.25rem',
              width: bigIcon ? '0.8rem' : '0.25rem'
            }}
          />
          <span>{storeAvailabilityOutOfStockWithStore}</span>
        </StoreAvailability>
      );
    }
    default: {
      return <StoreAvailability data-testid="store-availability-undefined"></StoreAvailability>;
    }
  }
};

export default GetInStoreStockIndicator;
